import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { useHistory, useLocation } from "react-router-dom";

import { Routes } from "@/config";
import { Icon } from "@/components";
import { useMeta } from "@/hooks";
import { Navigation, NavigationItem } from "@/ui";
import { QUERY_LIST_STORE_APPS } from "apollo/module-operations/app";

import {
    navigation as asideNavigation,
    toolBar,
} from "../../config/layout/aside";
import Header from "./header/Header";
import SubHeader from "./sub-header/SubHeader";
import { MUTATION_UI } from "../../apollo/mutations/local";
import { QUERY_UI } from "../../apollo/queries/local";
import layoutStyles from "./Layout.module.scss";

/** Strictly below 1024px — overlay drawer instead of in-flow rail. */
const COMPACT_MEDIA = "(max-width: 1023px)";

const Layout = () => {
    const { data } = useQuery(QUERY_UI);
    const [updateUi] = useMutation(MUTATION_UI);
    const history = useHistory();
    const location = useLocation();
    const { user } = useMeta();
    const [activeApps, setActiveApps] = useState([]);

    const ui = data?.ui;
    const asideMinimize = Boolean(ui?.asideMinimize);
    const uiRef = useRef(ui);
    uiRef.current = ui;

    const [isCompact, setIsCompact] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(COMPACT_MEDIA).matches
            : false,
    );

    useQuery(QUERY_LIST_STORE_APPS, {
        skip: user.loading || !user.accountId || !user.storeId,
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
        },
        onCompleted: resp => {
            if (resp?.listStoreApps) {
                setActiveApps(
                    resp.listStoreApps
                        .filter(app => app?.status)
                        .map(app => app?.handler) ?? [],
                );
            }
        },
    });

    const patchUi = patch => {
        const current = uiRef.current;
        updateUi({
            variables: {
                ui: {
                    asideHover: current?.asideHover ?? false,
                    asideMinimize: current?.asideMinimize ?? true,
                    asideToggle: current?.asideToggle ?? false,
                    headerToggle: current?.headerToggle ?? false,
                    ...patch,
                },
            },
        });
    };

    /* Breakpoint only — never depends on updateUi identity (that broke the toggle). */
    useEffect(() => {
        const media = window.matchMedia(COMPACT_MEDIA);

        const sync = matches => {
            setIsCompact(matches);
            if (matches) {
                /* Compact default: drawer closed. */
                patchUi({
                    asideMinimize: true,
                    asideHover: false,
                });
            }
        };

        sync(media.matches);
        const onChange = () => sync(media.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);

    const overlayOpen = isCompact && !asideMinimize;

    const closeOverlay = () => {
        patchUi({
            asideMinimize: true,
            asideHover: false,
        });
    };

    useEffect(() => {
        if (!overlayOpen) return;
        closeOverlay();
    }, [location.pathname]);

    useEffect(() => {
        if (!overlayOpen) return undefined;
        const onKeyDown = event => {
            if (event.key === "Escape") closeOverlay();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [overlayOpen]);

    const navigate = route => {
        if (
            typeof route === "string" &&
            (route.startsWith("http://") || route.startsWith("https://"))
        ) {
            if (overlayOpen) closeOverlay();
            window.location.assign(route);
            return;
        }
        history.push(route);
        if (overlayOpen) closeOverlay();
    };

    const navItems = asideNavigation.flatMap(entry => {
        if (entry.route) {
            return [
                {
                    label: entry.label,
                    route: entry.route,
                    icon: entry.icon,
                },
            ];
        }

        if (entry.items) {
            return entry.items
                .filter(item => typeof item.route === "string")
                .filter(
                    item => !item.handler || activeApps.includes(item.handler),
                )
                .map((item, index) => ({
                    label: item.label,
                    route: item.route,
                    icon: index === 0 ? entry.icon : undefined,
                }));
        }

        return [];
    });

    return (
        <main className={layoutStyles.root} data-ds-shell>
            <div className={layoutStyles.headerRow}>
                <Header />
            </div>
            <div
                className={layoutStyles.bodyRow}
                data-nav-compact={isCompact ? "true" : undefined}
                data-nav-overlay-open={overlayOpen ? "true" : undefined}
            >
                <button
                    aria-label="Close navigation"
                    className={layoutStyles.navScrim}
                    onClick={closeOverlay}
                    tabIndex={overlayOpen ? 0 : -1}
                    type="button"
                />
                <Navigation
                    aria-hidden={isCompact && asideMinimize ? true : undefined}
                    className={layoutStyles.navigationColumn}
                    expandOnHover={!isCompact}
                    rail={isCompact ? false : asideMinimize}
                >
                    <Navigation.List>
                        {navItems.map(item => {
                            const isActive =
                                location.pathname === item.route ||
                                location.pathname.startsWith(`${item.route}/`);
                            return (
                                <Navigation.ListItem key={item.route}>
                                    <NavigationItem
                                        active={isActive}
                                        icon={
                                            item.icon ? (
                                                <Icon icon={item.icon} />
                                            ) : undefined
                                        }
                                        onClick={() => navigate(item.route)}
                                    >
                                        {item.label}
                                    </NavigationItem>
                                </Navigation.ListItem>
                            );
                        })}
                    </Navigation.List>
                    <Navigation.Footer>
                        <Navigation.List listBehavior="anchor">
                            {toolBar.map(item => (
                                <Navigation.ListItem key={item.route}>
                                    <NavigationItem
                                        icon={
                                            item.icon ? (
                                                <Icon icon={item.icon} />
                                            ) : undefined
                                        }
                                        onClick={() => {
                                            if (item.target === "_blank") {
                                                window.open(
                                                    item.route,
                                                    "_blank",
                                                );
                                                if (overlayOpen) closeOverlay();
                                                return;
                                            }
                                            navigate(item.route);
                                        }}
                                    >
                                        {item.label}
                                    </NavigationItem>
                                </Navigation.ListItem>
                            ))}
                        </Navigation.List>
                    </Navigation.Footer>
                </Navigation>
                <div className={layoutStyles.routesColumn}>
                    <SubHeader />
                    <div
                        className={layoutStyles.dsRoutesRoot}
                        data-ds-content-root
                    >
                        <Routes />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Layout;
